import { type FC, type ReactNode, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sidebar, useSidebarCollapsed } from './Sidebar';
import { Sheet } from '../primitives/Sheet';
import { Topbar } from './Topbar';
import { Footer } from './Footer';
import { HamburgerSheet } from './HamburgerSheet';
import { useCurrentUser } from '@/auth/use-current-user';
import { useNotifications } from '@/hooks/use-notifications';

/**
 * AppShell — the common frame for every protected page in Agentic Loan
 * Audit. Sidebar, full-width top app bar, hamburger sheet. Each route
 * renders its page content inside the <main> slot provided by children.
 * The `title` prop names the page in the top app bar.
 */

export interface AppShellProps {
  /** crumb/search are accepted for caller compatibility but not rendered. */
  crumb?: string;
  /** Page title shown in the top app bar. */
  title: string;
  children: ReactNode;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

export const AppShell: FC<AppShellProps> = ({ title, children }) => {
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [hamburgerOpen, setHamburgerOpen] = useState(false);
  const navigate = useNavigate();
  const { user, signOut } = useCurrentUser();

  // Scroll keeper: when a Radix modal (Dialog) or popper (Select) opens while
  // its trigger lives inside the scroll container, Radix's scroll-lock resets
  // the container to the top. We remember the last scroll position and restore
  // it the moment a portal/dialog is added to the DOM, so opening any dialog
  // from a scrolled-down admin table keeps you exactly where you were.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let last = 0;
    let restoring = false;
    const onScroll = () => {
      if (!restoring) last = el.scrollTop;
    };
    el.addEventListener('scroll', onScroll, { passive: true });

    const isModalNode = (n: Node): boolean =>
      n instanceof HTMLElement &&
      (n.matches('[role="dialog"],[role="alertdialog"],[data-radix-popper-content-wrapper]') ||
        !!n.querySelector('[role="dialog"],[role="alertdialog"],[data-radix-popper-content-wrapper]'));

    const observer = new MutationObserver((mutations) => {
      const opened = mutations.some((m) => Array.from(m.addedNodes).some(isModalNode));
      if (opened && last > 0 && el.scrollTop < last) {
        restoring = true;
        const target = last;
        el.scrollTop = target;
        requestAnimationFrame(() => {
          el.scrollTop = target;
          requestAnimationFrame(() => {
            el.scrollTop = target;
            restoring = false;
          });
        });
      }
    });
    observer.observe(document.body, { childList: true });

    return () => {
      el.removeEventListener('scroll', onScroll);
      observer.disconnect();
    };
  }, []);

  const initials = (user?.displayName ?? user?.email ?? 'U')
    .split(/[\s@]+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || 'U';

  // Unread count drives the bell badge. The bell opens the full
  // notifications center (/notifications) — the single place to read,
  // mark, and delete notifications.
  const { unreadCount } = useNotifications();

  return (
    // Fixed-height shell: the MAIN COLUMN scrolls, not <body>. If the body
    // were the scroll container, Radix's modal scroll-lock (Dialog/Select)
    // would reset scroll to the top every time one opened. Keeping scroll on
    // an inner element makes opening a dialog preserve the scroll position.
    <div className="relative h-[100dvh] overflow-hidden flex bg-background">
      {/* Skip link — first focusable element; jumps past the chrome to the
          main content region. Visually hidden until focused. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:px-4 focus:py-2 focus:rounded-lg focus:bg-primary focus:text-on-primary focus:text-label focus:font-medium focus:shadow-glass-sm"
      >
        Skip to main content
      </a>
      {/* Desktop sidebar — fixed column */}
      <div className="hidden lg:flex h-[100dvh] z-20 shrink-0">
        <Sidebar collapsed={collapsed} onToggleCollapsed={toggleCollapsed} />
      </div>

      {/* Mobile sidebar — rendered inside a left-side Sheet */}
      <Sheet
        open={mobileSidebarOpen}
        onOpenChange={setMobileSidebarOpen}
        side="left"
        title="Navigation"
        widthPx={260}
      >
        <Sidebar variant="mobile" onNavigate={() => setMobileSidebarOpen(false)} />
      </Sheet>

      {/* Main column — top app bar + scroll container */}
      <div className="relative z-10 flex-1 min-w-0 flex flex-col">
        <Topbar
          title={title}
          initials={initials}
          onOpenMobileSidebar={() => setMobileSidebarOpen(true)}
          onOpenHamburger={() => setHamburgerOpen(true)}
          onOpenNotifications={() => navigate('/notifications')}
          hasUnreadNotifications={unreadCount > 0}
        />
        <div
          ref={scrollRef}
          className="flex-1 min-w-0 flex flex-col overflow-y-auto scrollbar-subtle"
        >
          <main
            id="main-content"
            tabIndex={-1}
            className="relative flex-1 px-4 lg:px-6 pt-4 pb-4 flex flex-col gap-4 outline-none"
          >
            {children}
          </main>

          <Footer />
        </div>
      </div>

      <HamburgerSheet
        open={hamburgerOpen}
        onOpenChange={setHamburgerOpen}
        user={{
          displayName: user?.displayName ?? 'Signed-in user',
          email: user?.email ?? '',
          initials,
        }}
        currentProduct="loanaudit"
        onSwitchProduct={(p) => {
          // Today all products are separate deployments. In the future
          // this becomes a real SSO redirect.
          // eslint-disable-next-line no-console
          console.info(`Product switch to ${p} not yet wired — separate deployment`);
        }}
        onSignOut={async () => {
          await signOut();
          navigate('/login');
        }}
      />
    </div>
  );
};

export default AppShell;
